import { z } from 'zod';

/**
 * The plugin update manifest's shape, safe for client code. `manifest.ts` builds the manifest on
 * the server (env, hashing); client hooks import from here so they don't pull in Node modules.
 */

export const pluginUpdateConnectorSchema = z.enum([
  'revit',
  'archicad',
  'rhino',
]);

export type PluginUpdateConnector = z.infer<typeof pluginUpdateConnectorSchema>;

export const pluginUpdateEntrySchema = z.object({
  /** Stable artifact id, matches PluginDownload.id */
  id: z.string(),
  connector: pluginUpdateConnectorSchema,
  platform: z.enum(['windows', 'macos']),
  /** Host application versions this artifact supports (e.g. Revit years, Archicad majors) */
  hostVersions: z.array(z.string()),
  /**
   * Latest downloadable plugin version, and the only version field the
   * update agents read: they compare it against the running version to
   * decide whether to stage an update. Moving it is what advances (or
   * rolls back) a rollout.
   */
  latestVersion: z.string(),
  /**
   * Version below which the app nudges users to update. App-side only —
   * it drives the in-app compat banner and nothing else. No update agent
   * consumes it (Revit's UpdateManifest.cs does not even deserialize it),
   * so changing it cannot start, stop, or steer an auto-update.
   */
  recommendedVersion: z.string(),
  /** Version below which the plugin is considered incompatible; null = no hard floor */
  minVersion: z.string().nullable(),
  downloadUrl: z.string().nullable(),
  filename: z.string(),
  /** Hex-encoded SHA-256 of the artifact; null until release CI publishes hashes */
  sha256: z.string().nullable(),
  /**
   * Raw install-folder zip the in-plugin update agent stages (content at
   * the zip root — the swapper swaps the extracted root in as the install
   * dir). Null until release CI/ops publish it; agents stay dormant.
   */
  payloadUrl: z.string().nullable(),
  payloadSha256: z.string().nullable(),
  /**
   * The arkyv-updater swapper artifact for this entry: a zip for revit
   * (its agent extracts), a raw executable for archicad (no zip handling
   * in the add-on). Rhino bundles the swapper at build time and leaves
   * these null.
   */
  swapperUrl: z.string().nullable(),
  swapperSha256: z.string().nullable(),
  /** Kill-switch: when false, plugin-side updaters must not auto-install this artifact */
  autoUpdate: z.boolean(),
  /**
   * When false — the default — the swapper skips its client-side hold-down
   * bookkeeping (the `unproven` counter and the resulting cooldown) and keeps
   * applying staged versions. Opt in per plugin/platform once the fleet is
   * large enough to want the brake. Server-side verdicts — the `autoUpdate`
   * kill switch and re-pointing `latestVersion` — remain the authoritative
   * brake either way (ENG-1257).
   */
  shouldHoldDown: z.boolean(),
});

export type PluginUpdateEntry = z.infer<typeof pluginUpdateEntrySchema>;

export const pluginUpdateManifestSchema = z.object({
  entries: z.array(pluginUpdateEntrySchema),
});

export type PluginUpdateManifest = z.infer<typeof pluginUpdateManifestSchema>;

/**
 * Recommended version for a connector from a fetched manifest, or null
 * when the manifest is unavailable (callers fall back to the build-time
 * constants).
 */
export function getConnectorRecommendedVersion(
  manifest: PluginUpdateManifest | undefined,
  connector: PluginUpdateConnector,
): string | null {
  const entry = manifest?.entries.find(
    (candidate) => candidate.connector === connector,
  );

  return entry?.recommendedVersion ?? null;
}
